import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LogoutModal from '../components/common/LogoutModal';
import EditScriptModal from '../components/common/EditScriptModal';
import DeleteScriptModal from '../components/common/DeleteScriptModal';
import Sidebar from '../components/Sidebar';
import { DocumnetTextIcon, MobileMenuIcon, MobileSearchIcon, StarrIcon, DeleteIcon } from '../assets/svgs/svgs';
import { fetchScripts, exportScript, deleteScript } from '../store/actions/scriptsActions';
import { logoutUser } from '../store/actions/authActions';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';

const MyScripts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false); 
  const [searchQuery, setSearchQuery] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptsPage, setScriptsPage] = useState(1);
  const itemsPerPage = 4;

  const { scripts, loading, error, deletingScript } = useSelector((state) => {
    return state.scripts;
  });

  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchScripts());
  }, [dispatch]);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleEditScript = (script) => {
    const scriptId = script?.id || script?._id;
    const latestScript = scripts.find(s => (s.id || s._id) === scriptId) || script;
    setSelectedScript(latestScript);
    setShowEditModal(true);
  };

  const handleDeleteScript = (script) => {
    setSelectedScript(script);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    const scriptId = selectedScript?.id || selectedScript?._id;
    if (scriptId) {
      const result = await dispatch(deleteScript(scriptId));
      if (result) {
        setShowDeleteModal(false);
        setSelectedScript(null);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedScript(null);
  };

  const handleCardClick = (script) => {
    const scriptId = script?.id || script?._id;
    if (scriptId) {
      navigate('/script-details', {
        state: {
          script: script,
          scriptId: scriptId
        }
      });
    }
  };

  const filteredScripts = scripts.filter(script => {
    const title = script?.topic?.title || script?.title || script?.name || '';
    const hook = script?.sequence?.hook?.text || '';
    const description = script?.topic?.description || script?.content || script?.description || '';
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = title.toLowerCase().includes(searchLower) ||
                         hook.toLowerCase().includes(searchLower) ||
                         description.toLowerCase().includes(searchLower);
    return matchesSearch;
  });

  const totalScriptsPages = Math.ceil(filteredScripts.length / itemsPerPage);

  const paginatedScripts = filteredScripts.slice(
    (scriptsPage - 1) * itemsPerPage,
    scriptsPage * itemsPerPage
  );

  useEffect(() => {
    setScriptsPage(1);
  }, [searchQuery]);

  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="my-scripts"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <MobileMenuIcon/>
            </button>

            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search scripts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-white placeholder:text-white/70 px-4 py-2.5 pr-10 rounded-lg border border-white bg-transparent focus:outline-none text-sm transition-all"
              />
              <MobileSearchIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 pointer-events-none"/>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-black">My Scripts</h1>
              <p className="text-secondary text-xs sm:text-sm mt-1">Here's All Scripts That You Have Created</p>
            </div>
            <button
              onClick={() => navigate('/create-script')}
              className="px-4 sm:px-6 py-2.5 btn-primary rounded-lg font-semibold transition-colors shadow-sm flex items-center justify-center gap-2 text-sm sm:text-base w-full sm:w-auto"
            >
              <span className="text-xl">+</span>
              Create New Script
            </button>
          </div>

          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-lg">

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                <svg className="w-16 h-16 mx-auto text-red-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Error loading scripts</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={() => dispatch(fetchScripts())}
                  className="px-6 py-3 bg-[#78350F] text-white rounded-xl font-semibold hover:bg-[#92400E] transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : filteredScripts.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                <DocumnetTextIcon/>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No scripts found</h3>
                <p className="text-secondary mb-6">Try adjusting your search or create a new script</p>
                <button
                  onClick={() => navigate('/create-script')}
                  className="px-6 py-3 btn-primary rounded-xl font-semibold transition-colors"
                >
                  Create New Script
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {paginatedScripts.map((script) => {
                  const scriptId = script?.id || script?._id;
                  const scriptTitle = script?.topic?.title || 'Untitled Script';
                  const scriptDescription = script?.sequence?.hook?.text || script?.topic?.description || script.content || '';
                  const userProfilePicture = user?.googleOAuth?.profilePicture || script?.user?.profilePicture || null;
                  const userName = user?.fullName || 'User';

                  return (
                    <div
                      key={scriptId}
                      className="bg-[#F5EBE0] rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleCardClick(script)}
                    >
                      {/* Top section - Profile pic left, Delete button right */}
                      <div className="flex justify-between items-start mb-4">
                        {/* Profile Picture - Left */}
                        <div className="w-36 h-36 rounded-lg overflow-hidden flex-shrink-0">
                          {userProfilePicture ? (
                            <img
                              src={userProfilePicture}
                              alt={userName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                              <span className="text-3xl font-bold text-amber-600">
                                {userName?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Delete button - top right */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteScript(script);
                          }}
                          className="p-2 bg-red-600/90 hover:bg-red-700 rounded-full backdrop-blur-sm transition-all group"
                          title="Delete Script"
                        >
                          <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:scale-110 transition-transform" />
                        </button>
                      </div>

                      {/* Title and description */}
                      <div className="mb-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">{scriptTitle}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{scriptDescription}</p>
                      </div>

                      {/* Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditScript(script);
                          }}
                          className="flex-1 py-3 bg-[#78350F] text-white rounded-[10px] font-semibold hover:bg-[#5A2709] transition-colors text-sm"
                        >
                          EDIT SCRIPT
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(exportScript(scriptId));
                          }}
                          className="flex-1 py-3 bg-[#78350F] text-white rounded-[10px] font-semibold hover:bg-[#5A2709] transition-colors text-sm"
                        >
                          EXPORT SCRIPT
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>

                {filteredScripts.length > itemsPerPage && (
                  <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setScriptsPage(prev => Math.max(1, prev - 1))}
                      disabled={scriptsPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {scriptsPage} of {totalScriptsPages}
                    </span>
                    <button
                      onClick={() => setScriptsPage(prev => Math.min(totalScriptsPages, prev + 1))}
                      disabled={scriptsPage >= totalScriptsPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
            <LogoutModal 
              showLogoutModal={showLogoutModal}
              onConfirm={confirmLogout}
              onCancel={cancelLogout}
            />
            <EditScriptModal
              showModal={showEditModal}
              onClose={() => {
                setShowEditModal(false);
                setSelectedScript(null);
              }}
              script={selectedScript}
            />
            <DeleteScriptModal
              showDeleteModal={showDeleteModal}
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
              isLoading={deletingScript}
            />
          </div>
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default MyScripts;